import { useTranslation } from 'react-i18next';

import DEFAULTVALUE from '@/app/constants/defaultValue';
import { HumanResourcesItem } from '@/app/models/detailsRH';
import { useUpdateHumanResourcesMutation } from '@/app/services/detailsHRApi';
import { useSearchSchoolLavelQuery } from '@/app/services/financialClassificationApi';
import formatterMonetaryValue, {
  cpfOrCpnjFromBack,
  formatHandleCPFAndCNPJ,
  isSupplierCodeValid,
} from '@/app/utils/numberUtils';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import useFormattedCpfAndCpnj from '@/hooks/useFormattedCpfAndCpnj';
import { Box, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import FormFieldLong from '../baseForms/FormFieldLong';
import FormFieldSelect from '../baseForms/FormFieldSelect';
import InfoTotalValue from '../baseForms/info/InfoTotalValue';

interface NewEditHumanResourcesProps {
  selectItem: HumanResourcesItem | null;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditHumanResourcesDetailsPJ(props: NewEditHumanResourcesProps) {
  const theme = useTheme();
  const { t } = useTranslation(['humanResource', 'common']);

  const { selectItem, setDrawerOpen } = props;

  const [graduationLevel, setGraduationLevel] = useState<string>(
    selectItem?.graduationLevel ? selectItem?.graduationLevel?.id.toString() : '',
  );

  const { data: schoolLevel } = useSearchSchoolLavelQuery();

  const [workedHours, setWorkedHours] = useState<number>(
    selectItem?.monthlyJourney ? selectItem.monthlyJourney : DEFAULTVALUE,
  );

  const { value: cpf, setValue: setCpf } = useFormattedCpfAndCpnj(selectItem?.cpf);
  const [collaborator, setCollaborator] = useState<string>(selectItem?.name != null ? selectItem?.name : '');
  const [position, setPosition] = useState<string>(selectItem?.positionCode ? selectItem?.positionCode : '');
  const [graduationCourse, setGraduationCourse] = useState<string>(
    selectItem?.graduationCourseCode ? selectItem?.graduationCourseCode : '',
  );
  const [descriptionActivities, setDescriptionActivities] = useState<string>(
    selectItem?.descriptionActivities ? selectItem?.descriptionActivities : '',
  );

  const handleColaboratorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCollaborator(e.target.value);
  };
  const handlePositionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPosition(e.target.value);
  };

  const [update] = useUpdateHumanResourcesMutation();

  const handleSubmit = async () => {
    if (!selectItem) return;
    setDrawerOpen(false);
    await toast.promise(
      update({
        id: selectItem?.id as number,
        personName: selectItem?.name,
        personBadge: selectItem?.badge,
        cpf: cpfOrCpnjFromBack(cpf),
        graduationCourseCode: graduationCourse,
        graduationLevel: {
          id: Number(graduationLevel),
        },
        monthlyJourney: workedHours,
        descriptionActivities,
        position,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('hr.updated.successfully') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const handleSupplierCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatHandleCPFAndCNPJ(e.target.value);
    setCpf(formatted);
  };
  const supplierCodeIsValid = isSupplierCodeValid(cpf);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('edit.detail.pj.service')} />
        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 1 }} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <InfoTotalValue
            label={t('common:total.value') as string}
            value={formatterMonetaryValue(selectItem?.salary as number)}
          />
        </Box>
        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />
        <Box sx={{ mb: 2, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}
          >
            <Typography sx={{ width: theme.spacing(12), mb: 0 }} variant="fieldHeader">
              {t('common:collaborator')}
            </Typography>
            <TextField
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              size="small"
              value={collaborator}
              onChange={handleColaboratorChange}
            />
          </Stack>
        </Box>
        <Box sx={{ mb: 2, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}
          >
            <Typography sx={{ width: theme.spacing(12), mb: 0 }} variant="fieldHeader">
              {t('cpf')}
            </Typography>
            <TextField
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              size="small"
              value={cpf}
              onChange={handleSupplierCode}
            />
          </Stack>
        </Box>

        <Box sx={{ mb: 2, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}
          >
            <Typography sx={{ width: theme.spacing(12), mb: 0 }} variant="fieldHeader">
              {t('position')}
            </Typography>
            <TextField
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              size="small"
              value={position}
              onChange={handlePositionChange}
            />
          </Stack>
        </Box>

        <Box sx={{ mb: 2, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}
          >
            <Typography sx={{ width: theme.spacing(12), mb: 0 }} variant="fieldHeader">
              {t('graduation.course')}
            </Typography>
            <TextField
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              size="small"
              value={graduationCourse}
              onChange={(e) => setGraduationCourse(e.target.value)}
            />
          </Stack>
        </Box>

        <FormFieldSelect
          label={t('graduation.level')}
          value={graduationLevel}
          onChange={setGraduationLevel}
          options={schoolLevel ?? []}
          placeholder={t('select.school.level') as string}
        />

        <Box sx={{ mb: 2, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}
          >
            <Typography sx={{ width: theme.spacing(12), mb: 0 }} variant="fieldHeader">
              {t('worked.hours')}
            </Typography>
            <TextField
              type="number"
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              size="small"
              value={workedHours}
              onChange={(e) => setWorkedHours(parseFloat(e.target.value))}
            />
          </Stack>
        </Box>
        <Typography component="p" sx={{ mt: 1 }}>
          {t('total.worked.hours.current.period')}
        </Typography>

        <FormFieldLong
          label={t('activity')}
          value={descriptionActivities}
          onChange={(e) => setDescriptionActivities(e.target.value)}
        />
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={!supplierCodeIsValid} />
      </Box>
    </Box>
  );
}
